import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Badge,
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, Pencil } from 'lucide-react';
import { client, listPermissions } from '../../services/api';
import type { PermissionResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import EditPermissionDialog from './EditPermissionDialog';

export type PermissionsTableHandle = {
  refresh: () => void;
};

const PermissionsTable = forwardRef<PermissionsTableHandle>((_, ref) => {
  const { showError } = useStore(
    useShallow(state => ({
      showError: state.showError,
    }))
  );

  const [permissions, setPermissions] = useState<PermissionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPerm, setEditingPerm] = useState<PermissionResponse | null>(
    null
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listPermissions({
        client,
        query: { limit: 500 },
      });
      setPermissions(res.data ?? []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      showError(
        'Failed to Load Permissions',
        'Unable to load permissions. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useImperativeHandle(ref, () => ({ refresh: load }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <CardContent>
      {isLoading ? (
        <div className='flex items-center justify-center py-10'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-sm text-muted-foreground'>
            Loading permissions...
          </span>
        </div>
      ) : permissions.length === 0 ? (
        <p className='text-sm text-muted-foreground py-6 text-center'>
          No permissions found.
        </p>
      ) : (
        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow className='hover:bg-transparent'>
                <TableHead className='w-48'>Name</TableHead>
                <TableHead className='w-48'>Slug</TableHead>
                <TableHead className='w-24'>Scope</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className='w-16' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map(perm => (
                <TableRow
                  key={perm.id}
                  className='even:bg-muted/50 odd:hover:bg-transparent even:hover:bg-muted/50'
                >
                  <TableCell className='font-medium'>{perm.name}</TableCell>
                  <TableCell className='font-mono text-xs text-muted-foreground'>
                    {perm.slug}
                  </TableCell>
                  <TableCell>
                    <Badge variant='outline' className='capitalize'>
                      {perm.scope.toLowerCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className='text-sm text-muted-foreground'>
                    {perm.description}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setEditingPerm(perm)}
                      aria-label={`Edit ${perm.name}`}
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editingPerm && (
        <EditPermissionDialog
          permission={editingPerm}
          open={editingPerm !== null}
          onClose={() => setEditingPerm(null)}
          onUpdated={load}
        />
      )}
    </CardContent>
  );
});

PermissionsTable.displayName = 'PermissionsTable';

export default PermissionsTable;
